import { filterFlaggedIssues } from './flaggedTrx';
import { findDataElementByTitle, logger } from './utils';
import { createTotalColumn, createChartsValues } from './utilityData';
import { ReportColumn, ReportViewModel, AuditResult, Column } from './types';
import {
  AskRequest,
  Location as KsLocation,
  ReportTimeframe,
  ReportTypes,
  ReportConfiguration,
} from '@hone-automation/common';
import { periodIndex } from './periods';
import { Option } from 'types';
import { createReportViewModel } from './transformedData';
import { createAccountsColumn } from './accountsColumn';
import { compileTransactions } from './transactions';
import { generateAIAnalysisData } from './aiAnalysis';

// This is going to be a streamlined version of the NT parser
// It will be used to parse the NT report and create the intermediate data structure that will be used in all exports.

type ParseReportNTProps = {
  report: NestedHoneReport;
  reportId?: string;
  useComputedColumns?: boolean;
  periodReporting?: boolean;
  reportCFG?: ReportConfiguration;
  flaggedTransactions?: AskRequest[];
  currentLocation?: KsLocation;
  budget?: boolean;
  trace?: boolean;
  reversedReport?: boolean;
  consolidated?: boolean;
  extendedAnalysis?: boolean;
  requestedTimeframe?: ReportTimeframe;
  /** @deprecated No longer used - feature flags for v1/v2 schema have been removed */
  launchDarklyFlags?: any;
};

export const parseReportNT = ({
  report,
  reportId,
  useComputedColumns,
  periodReporting = false,
  reportCFG,
  flaggedTransactions,
  currentLocation,
  budget = false,
  trace = true,
  reversedReport = false,
  consolidated = false,
  extendedAnalysis = false,
  requestedTimeframe,
}: ParseReportNTProps): ReportViewModel | null => {
  if (trace) {
    logger.msg('reportCFG', reportCFG);
    logger.msg('report', report);
    logger.msg('flaggedTransactions', flaggedTransactions);
    // eslint-disable-next-line no-console
    console.time('parseReportNT');
  }

  if (!report || !currentLocation) {
    logger.error('(silent error) NT:parseReportNT: Missing report or currentLocation');
    return null;
  }

  if (!report.dates || !report.dates.length) {
    logger.error('(silent error) NT:parseReportNT: Original Report does not have dates', report);
    return null;
  }

  // Get the period titles
  const periodTitles: Option[] | null = periodReporting
    ? periodIndex({
        limit: report?.dates.length + 20,
        currentLocation,
        reportCFG,
      })
    : null;

  // Create the transformed data object
  const transformedData: ReportViewModel | null = createReportViewModel({
    report,
    reportId,
    useComputedColumns,
    currentLocation,
    periodReporting: periodReporting && report.timeframe !== 'Weekly',
    periodTitles,
    reportCFG,
    budget,
    consolidated,
  });

  if (!transformedData) {
    logger.error('(silent error) NT:: Failed to parse report', report);
    return null;
  }

  // Filter flagged data - uses AskRequest format directly from backend
  transformedData.queueIssues = filterFlaggedIssues(report, flaggedTransactions);

  // Create the accounts column and fill in the data property of every column after we mapped budget properly
  const accounts: ReportColumn = createAccountsColumn(report, transformedData);
  // Adding the baseValues where the percentages come from to the amounts, so we can create total and difference percentages accurately
  applyBaseValues(transformedData);

  // Create aggregates (total/difference columns)
  transformedData.aggregates = {
    total: createTotalColumn(transformedData),
    difference: createTotalColumn(transformedData, true),
    totalRow: [],
  };

  // We add the accounts column to the beginning of the columns array, only after we have processed the rest of data transformations
  transformedData.columns.unshift(accounts);

  // Backend handles all column reversal - no frontend reversal needed
  // Data and dates arrive from backend already in correct order

  // Create chart data
  const chartData = createChartsValues(transformedData, report);
  if (!transformedData.analysis) {
    transformedData.analysis = {};
  }
  transformedData.analysis.chartData = chartData;

  transformedData.renderableColumns++;

  if (extendedAnalysis && report?.type === ReportTypes.PLComparison) {
    // Create audit data
    const transactions = compileTransactions(transformedData);
    if (!transformedData.audit) {
      transformedData.audit = {};
    }
    transformedData.audit.transactions = transactions;

    try {
      const aiInsights = generateAIAnalysisData(report, currentLocation?.weekStart, reportCFG, requestedTimeframe);
      if (!transformedData.analysis) {
        transformedData.analysis = {};
      }
      transformedData.analysis.aiInsights = aiInsights;
    } catch (error: any) {
      logger.error('(silent error) NT:: Failed to generate AI Analysis Data', error);
    }
    // Integrate audit results into amounts data for vendor invoices
    if (isVendorInvoicesReport(report) || isBalanceSheetReport(report)) {
      integrateAuditResultsIntoAmounts(transformedData);
    }
  }
  if (isBalanceSheetReport(report)) {
    const transactions = compileTransactions(transformedData);
    if (!transformedData.audit) {
      transformedData.audit = {};
    }
    transformedData.audit.transactions = transactions;

    integrateAuditResultsIntoAmounts(transformedData);
  }

  if (trace) {
    logger.msg('transformedData', transformedData);
    // eslint-disable-next-line no-console
    console.timeEnd('parseReportNT');
  }

  return transformedData;
};

const applyBaseValues = (transformedData: ReportViewModel): any => {
  // Adding the baseValues where the percentages come from to the amounts, so we can create total and difference percentages accurately
  transformedData.columns
    .filter((column: Column) => !column.compoundColumn)
    .forEach((column: any, columnIndex: number) => {
      column.data.forEach((amount: any, rowId: number) => {
        const baseValue = findDataElementByTitle(column.data, amount.baseRef);
        if (baseValue) {
          amount.baseAmount = baseValue.amount;
          amount.cPercent = amount.amount / amount.baseAmount;
        } else {
          //console.log('(silent error) NT:: Failed to find baseValue for ',amount.baseRef);
        }
      });
    });
};

const isVendorInvoicesReport = (report: NestedHoneReport): boolean => {
  // Check if this is a vendor invoices report
  return report.type === 'AP Aging' || report.title?.includes('Vendor') || report.title?.includes('Invoice') || false;
};

const isBalanceSheetReport = (report: NestedHoneReport): boolean => {
  return report.type === 'Balance Sheet' || report.title?.includes('Balance Sheet') || false;
};

const integrateAuditResultsIntoAmounts = (transformedData: ReportViewModel): void => {
  const transactions = transformedData.audit?.transactions;
  if (!transactions) return;

  const auditResults = transactions.auditedTransactions;

  // Iterate through all columns (excluding the first accounts column)
  transformedData.columns.slice(1).forEach((column: Column) => {
    if (column.data && Array.isArray(column.data)) {
      column.data.forEach((amount: any, rowIndex: number) => {
        // Find matching audit results for this amount based on title, date, or account
        const matchingAudit = findMatchingAuditResult(amount, auditResults);
        if (matchingAudit) {
          amount.auditResult = matchingAudit;
        }
      });
    }
  });
};

const findMatchingAuditResult = (amount: any, auditResults: any): AuditResult | null => {
  if (amount.title && (amount.title.includes('vendor') || amount.title.includes('invoice'))) {
    return {
      fieldName: amount.title,
      reportName: amount.refTitle || 'Vendor Invoice Report',
      type: 'Warning',
      message: `Audit result for ${amount.title}`,
      reportValue: amount.amount,
      mean: amount.amountAVG,
      perc: amount.percent !== undefined,
    };
  }
  return null;
};
